import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import PricingSection from '../components/PricingSection';
import { ArrowRight } from 'lucide-react';

const HOW_IT_WORKS = [
  {
    title: 'A single probability score',
    text: 'For every itinerary, MIZAR returns the probability that the fare will rise by 10% or more within 7 days — as a score between 0 and 1. Your platform decides how to surface it.'
  },
  {
    title: 'Built on live UK route data',
    text: '10,000+ price observations captured since February 2026. 153 daily snapshots across 9 UK airports. 28% of monitored routes showed a 10%+ price rise within 7 days.'
  },
  {
    title: 'Why it changes behaviour',
    text: 'People act faster to avoid loss than to chase savings. "Pay £80 more if you wait" converts. A countdown timer destroys trust. Genuine signal does neither.'
  },
  {
    title: 'One API call. Live in a day.',
    text: 'A single POST request returns a score and recommendation. No SDK. No model training. No data pipeline to build. White-label ready — your users never know we exist.'
  }
];

const VALIDATION = [
  {
    title: 'Every prediction verified at t+7',
    text: 'Automated outcome tracking checks every prediction seven days after it was made. Precision and recall metrics update continuously — not projected.'
  },
  {
    title: 'You can see how it performs',
    text: "Before you commit to anything, we pull live pricing data for routes relevant to your platform and show you what the signal would have flagged."
  },
  {
    title: 'Crisis-aware by design',
    text: 'When market disruptions occur — fuel shocks, geopolitical events, airspace closures — contaminated periods are automatically excluded from model training. The signal stays clean.'
  },
  {
    title: 'Live since March 2026',
    text: 'Not a prototype. A live prediction API serving production traffic across 104 UK routes. Sub-50ms response times on every request.'
  }
];

function Eyebrow({ label }) {
  return (
    <div className="eyebrow">
      <span className="eyebrow-dot" />
      {label}
    </div>
  );
}

function FeatureList({ items, active, onSelect }) {
  return (
    <div className="feature-list">
      {items.map((item, i) => (
        <div
          key={i}
          className={`feature-item${active === i ? ' active' : ''}`}
          onClick={() => onSelect(i)}
        >
          <div className="feature-title">{item.title}</div>
          {active === i && <div className="feature-text">{item.text}</div>}
        </div>
      ))}
    </div>
  );
}

export default function LandingPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeHow, setActiveHow] = useState(0);
  const [activeVal, setActiveVal] = useState(0);

  useEffect(() => {
    const theme = localStorage.getItem('theme') || 'light';
    document.documentElement.setAttribute('data-theme', theme);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="lp">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600&display=swap');

        :root,
        :root[data-theme="light"] {
          --bg:      #F4F1EB;
          --bg2:     #FDFCF8;
          --bg3:     #EAE7DF;
          --ink:     #1C1917;
          --ink2:    #6B6560;
          --ink3:    #A8A19A;
          --fire:    #D97642;
          --line:    rgba(28,25,23,0.09);
        }

        :root[data-theme="dark"] {
          --bg:      #100E0C;
          --bg2:     #1A1714;
          --bg3:     #242018;
          --ink:     #F0EDE6;
          --ink2:    #9E9890;
          --ink3:    #665F58;
          --fire:    #D97642;
          --line:    rgba(240,237,230,0.08);
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }

        .lp {
          min-height: 100vh;
          background: var(--bg);
          color: var(--ink);
          font-family: 'DM Sans', sans-serif;
        }

        /* ── EYEBROW ─────────────────────────── */
        .eyebrow {
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          font-family: 'Manrope', sans-serif;
          font-size: 0.7rem;
          font-weight: 600;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--ink2);
          margin-bottom: 1.75rem;
        }
        .eyebrow-dot {
          width: 7px;
          height: 7px;
          border-radius: 50%;
          background: var(--fire);
          flex-shrink: 0;
        }

        /* ── HERO ────────────────────────────── */
        .hero {
          max-width: 1120px;
          margin: 0 auto;
          padding: 9rem 2rem 7rem;
        }
        .hero-h1 {
          font-family: 'Abril Fatface', serif;
          font-weight: 400;
          font-size: clamp(2.8rem, 5.8vw, 5.25rem);
          line-height: 1.05;
          letter-spacing: -0.028em;
          color: var(--ink);
          max-width: 800px;
          margin-bottom: 1.75rem;
        }
        .hero-sub {
          font-size: clamp(1rem, 1.5vw, 1.125rem);
          color: var(--ink2);
          max-width: 500px;
          line-height: 1.8;
          font-weight: 400;
          margin-bottom: 2.75rem;
        }
        .hero-ctas {
          display: flex;
          gap: 0.875rem;
          flex-wrap: wrap;
        }

        /* ── BUTTONS ─────────────────────────── */
        .btn-primary {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.8rem 1.5rem;
          background: var(--ink);
          color: var(--bg);
          border: 1px solid var(--ink);
          border-radius: 0.35rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .btn-primary:hover { opacity: 0.75; }

        .btn-secondary {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.8rem 1.5rem;
          background: transparent;
          color: var(--ink2);
          border: 1px solid var(--line);
          border-radius: 0.35rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
        }
        .btn-secondary:hover { border-color: var(--ink2); color: var(--ink); }

        /* ── STATS BAR ───────────────────────── */
        .stats-bar {
          border-top: 1px solid var(--line);
          border-bottom: 1px solid var(--line);
          padding: 2.5rem 2rem;
        }
        .stats-inner {
          max-width: 1120px;
          margin: 0 auto;
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 2rem;
        }
        .stat-num {
          font-family: 'Abril Fatface', serif;
          font-weight: 400;
          font-size: clamp(1.75rem, 2.8vw, 2.5rem);
          letter-spacing: -0.025em;
          color: var(--ink);
          line-height: 1;
          margin-bottom: 0.45rem;
        }
        .stat-label {
          font-size: 0.82rem;
          color: var(--ink3);
          font-weight: 400;
          letter-spacing: 0.01em;
        }

        /* ── SPLIT SECTION ───────────────────── */
        .split {
          max-width: 1120px;
          margin: 0 auto;
          padding: 6.5rem 2rem;
          display: grid;
          grid-template-columns: 5fr 7fr;
          gap: 6rem;
          align-items: start;
        }
        .split-h2 {
          font-family: 'Abril Fatface', serif;
          font-weight: 400;
          font-size: clamp(1.75rem, 2.6vw, 2.5rem);
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: var(--ink);
          margin-bottom: 1.25rem;
        }
        .split-desc {
          font-size: 0.975rem;
          color: var(--ink2);
          line-height: 1.8;
          max-width: 300px;
        }

        /* ── FEATURE LIST ────────────────────── */
        .feature-list {
          display: flex;
          flex-direction: column;
        }
        .feature-item {
          padding: 1.375rem 0 1.375rem 1.5rem;
          border-left: 2px solid var(--line);
          cursor: pointer;
          transition: border-color 0.2s;
        }
        .feature-item.active {
          border-left-color: var(--fire);
        }
        .feature-title {
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 0.975rem;
          color: var(--ink3);
          line-height: 1.4;
          transition: color 0.2s;
        }
        .feature-item.active .feature-title {
          color: var(--ink);
        }
        .feature-item:hover:not(.active) .feature-title {
          color: var(--ink2);
        }
        .feature-text {
          font-size: 0.9rem;
          color: var(--ink2);
          line-height: 1.75;
          margin-top: 0.65rem;
        }

        /* ── DIVIDER ─────────────────────────── */
        .rule {
          max-width: 1120px;
          margin: 0 auto;
          padding: 0 2rem;
        }
        .rule hr {
          border: none;
          border-top: 1px solid var(--line);
        }

        /* ── PROSE ───────────────────────────── */
        .prose-wrap {
          max-width: 1120px;
          margin: 0 auto;
          padding: 6.5rem 2rem;
          display: grid;
          grid-template-columns: 5fr 7fr;
          gap: 6rem;
          align-items: start;
        }
        .prose-h2 {
          font-family: 'Abril Fatface', serif;
          font-weight: 400;
          font-size: clamp(1.75rem, 2.6vw, 2.5rem);
          line-height: 1.08;
          letter-spacing: -0.02em;
          color: var(--ink);
          margin-bottom: 1.25rem;
        }
        .prose-body p {
          font-size: 1rem;
          color: var(--ink2);
          line-height: 1.8;
          margin-bottom: 1.25rem;
        }
        .prose-body p:last-child {
          margin-bottom: 0;
        }
        .prose-pull {
          border-left: 3px solid var(--fire);
          padding: 0.25rem 0 0.25rem 1.25rem;
          margin: 1.75rem 0;
          font-family: 'Manrope', sans-serif;
          font-weight: 700;
          font-size: 1.05rem;
          color: var(--ink);
          line-height: 1.4;
          letter-spacing: -0.01em;
        }

        /* ── CTA BAND ────────────────────────── */
        .cta-band {
          background: var(--ink);
          color: var(--bg);
          padding: 6rem 2rem;
          text-align: center;
        }
        .cta-band .eyebrow {
          color: rgba(240,237,230,0.4);
          justify-content: center;
        }
        .cta-h2 {
          font-family: 'Abril Fatface', serif;
          font-weight: 400;
          font-size: clamp(1.9rem, 3vw, 2.85rem);
          line-height: 1.08;
          letter-spacing: -0.025em;
          max-width: 580px;
          margin: 0 auto 1.25rem;
        }
        .cta-sub {
          font-size: 1rem;
          line-height: 1.8;
          color: rgba(240,237,230,0.6);
          max-width: 460px;
          margin: 0 auto 2.5rem;
        }
        .cta-btns {
          display: flex;
          gap: 0.875rem;
          justify-content: center;
          flex-wrap: wrap;
        }
        .btn-fire {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.8rem 1.5rem;
          background: var(--fire);
          color: #fff;
          border: 1px solid var(--fire);
          border-radius: 0.35rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 600;
          font-size: 0.9rem;
          cursor: pointer;
          transition: opacity 0.15s;
        }
        .btn-fire:hover { opacity: 0.85; }
        .btn-ghost-inv {
          display: inline-flex;
          align-items: center;
          gap: 0.45rem;
          padding: 0.8rem 1.5rem;
          background: transparent;
          color: rgba(240,237,230,0.65);
          border: 1px solid rgba(240,237,230,0.2);
          border-radius: 0.35rem;
          font-family: 'DM Sans', sans-serif;
          font-weight: 500;
          font-size: 0.9rem;
          cursor: pointer;
          transition: border-color 0.15s, color 0.15s;
        }
        .btn-ghost-inv:hover {
          color: rgba(240,237,230,1);
          border-color: rgba(240,237,230,0.45);
        }

        :root[data-theme="dark"] .cta-band .eyebrow {
          color: rgba(28,25,23,0.45);
        }
        :root[data-theme="dark"] .cta-h2 {
          color: #1C1917;
        }
        :root[data-theme="dark"] .cta-sub {
          color: rgba(28,25,23,0.6);
        }
        :root[data-theme="dark"] .btn-ghost-inv {
          color: rgba(28,25,23,0.65);
          border-color: rgba(28,25,23,0.2);
        }
        :root[data-theme="dark"] .btn-ghost-inv:hover {
          color: rgba(28,25,23,1);
          border-color: rgba(28,25,23,0.45);
        }

        /* ── RESPONSIVE ─────────────────────── */
        @media (max-width: 900px) {
          .split,
          .prose-wrap {
            grid-template-columns: 1fr;
            gap: 3rem;
            padding: 4.5rem 1.5rem;
          }
          .split-desc { max-width: 100%; }
          .stats-inner { grid-template-columns: 1fr 1fr; }
        }

        @media (max-width: 600px) {
          .hero { padding: 7rem 1.5rem 5rem; }
          .hero-h1 { font-size: 2.5rem; }
          .hero-ctas { flex-direction: column; }
          .btn-primary,
          .btn-secondary { width: 100%; justify-content: center; }
          .stats-inner { grid-template-columns: 1fr; }
          .cta-btns { flex-direction: column; align-items: center; }
          .btn-fire,
          .btn-ghost-inv { width: 100%; max-width: 320px; justify-content: center; }
        }
      `}</style>

      <Navigation />

      {/* ── HERO ──────────────────────────────────────── */}
      <section className="hero">
        <Eyebrow label="Flight Pricing Intelligence — API" />
        <h1 className="hero-h1">
          You don't need more traffic.<br />
          You need users to stop hesitating.
        </h1>
        <p className="hero-sub">
          Most people don't book because they think waiting might save money.
          Most of the time, it doesn't. MIZAR shows when waiting is likely
          to cost more — so users act on data, not anxiety.
        </p>
        <div className="hero-ctas">
          {user ? (
            <button onClick={() => navigate('/console')} className="btn-primary">
              Access Console <ArrowRight size={16} />
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/signup')} className="btn-primary">
                Request a data sample <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate('/implementation')} className="btn-secondary">
                Documentation
              </button>
            </>
          )}
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────── */}
      <div className="stats-bar">
        <div className="stats-inner">
          <div>
            <div className="stat-num">10,000+</div>
            <div className="stat-label">Price observations</div>
          </div>
          <div>
            <div className="stat-num">153/day</div>
            <div className="stat-label">Route snapshots</div>
          </div>
          <div>
            <div className="stat-num">28%</div>
            <div className="stat-label">Routes flagged for price rise</div>
          </div>
        </div>
      </div>

      {/* ── HOW IT WORKS ──────────────────────────────── */}
      <div className="split">
        <div>
          <Eyebrow label="How it works" />
          <h2 className="split-h2">One signal.<br />Integrated in a day.</h2>
          <p className="split-desc">
            A single API call returns the probability that a flight price will rise
            by 10% or more within seven days. Your platform decides how to use it.
          </p>
        </div>
        <FeatureList items={HOW_IT_WORKS} active={activeHow} onSelect={setActiveHow} />
      </div>

      <div className="rule"><hr /></div>

      {/* ── THE PROBLEM ───────────────────────────────── */}
      <div className="prose-wrap">
        <div>
          <Eyebrow label="The problem" />
          <h2 className="prose-h2">The problem isn't price.<br />It's timing.</h2>
        </div>
        <div className="prose-body">
          <p>
            Your users are ready to book. They just don't know if now is the right moment.
            Should they wait? Will it drop? Are they overpaying?
          </p>
          <p>So they hesitate. And hesitation kills conversion.</p>
          <div className="prose-pull">Waiting feels safe. It usually isn't.</div>
          <p>
            People delay because it feels like the low-risk option. In reality, flight
            prices are more likely to rise than fall. The cost of waiting is just harder
            to see — until it's too late.
          </p>
        </div>
      </div>

      <div className="rule"><hr /></div>

      {/* ── VALIDATION ────────────────────────────────── */}
      <div className="split">
        <div>
          <Eyebrow label="Validation" />
          <h2 className="split-h2">We track what happens next.</h2>
          <p className="split-desc">
            Every prediction is verified at t+7 days against the actual price.
            The numbers are not projected — they are measured.
          </p>
        </div>
        <FeatureList items={VALIDATION} active={activeVal} onSelect={setActiveVal} />
      </div>

      {/* ── PRICING ───────────────────────────────────── */}
      <PricingSection />

      {/* ── CTA BAND ──────────────────────────────────── */}
      <div className="cta-band">
        <Eyebrow label="Get started" />
        <h2 className="cta-h2">See what MIZAR flags on your routes</h2>
        <p className="cta-sub">
          We will pull a week of live pricing data for routes relevant to your platform
          and show you what the signal would have flagged. No commitment required.
        </p>
        <div className="cta-btns">
          {user ? (
            <button onClick={() => navigate('/console')} className="btn-fire">
              Access Console <ArrowRight size={16} />
            </button>
          ) : (
            <>
              <button onClick={() => navigate('/signup')} className="btn-fire">
                Request a data sample <ArrowRight size={16} />
              </button>
              <button onClick={() => navigate('/implementation')} className="btn-ghost-inv">
                Documentation
              </button>
            </>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
